<!-- src/components/RouletteWheel.vue -->
<template>
  <div class="main-container">
    <img
      :src="backgroundImg"
      class="background-image"
      alt="Cassino Background"
    />

    <!-- Cabeçalhos traduzidos -->
    <h1 class="main-title">{{ t("roulette.headerTitle") }}</h1>
    <h1 class="main-title-sub">{{ t("roulette.headerSubPoints") }}</h1>
    <h1 class="main-title-sub1">{{ t("roulette.headerSubBonus") }}</h1>

    <div class="content-wrapper">
      <div class="left-section">
        <div v-if="user" class="user-info">
          <p>
            <strong>{{ t("roulette.user") }}</strong> {{ user.mid }}
          </p>
          <ul>
            <li>{{ t("roulette.pointsLabel") }}: {{ user.pvalues }}</li>
            <li>{{ t("roulette.bonusLabel") }}: {{ user.bonus }}</li>
          </ul>
        </div>
        <button
          class="currency-button"
          :class="{ active: selectedCurrency === 'pvalues' }"
          @click="selectedCurrency = 'pvalues'"
          :disabled="isSpinning || !user"
        >
          <img :src="zephyrCoinsImg" alt="Zephyr Coins" />
        </button>
        <button
          class="currency-button"
          :class="{ active: selectedCurrency === 'bonus' }"
          @click="selectedCurrency = 'bonus'"
          :disabled="isSpinning || !user"
        >
          <img :src="bonusCoinsImg" alt="Bonus Coins" />
        </button>
      </div>

      <div class="right-section">
        <h2 class="text-h4">{{ t("roulette.title") }}</h2>

        <div class="wheel-container" ref="wheelContainer">
          <img :src="wheelImg" class="wheel" alt="Wheel Base" />

          <div
            class="rotating-circle"
            :style="{
              transform: `rotate(${currentRotation}deg)`,
              transition: isSpinning
                ? `transform ${spinDuration}s cubic-bezier(0.33,1,0.68,1)`
                : 'none'
            }"
            ref="rotator"
          >
            <svg
              class="segment-lines"
              viewBox="0 0 100 100"
              preserveAspectRatio="xMidYMid meet"
            >
              <g
                v-for="(seg, i) in segments"
                :key="i"
                :transform="`rotate(${i * (360 / segments.length)} 50 50)`"
              >
                <line
                  x1="50"
                  y1="50"
                  x2="50"
                  y2="0"
                  stroke="#fff"
                  stroke-width="0.5"
                />
              </g>
            </svg>

            <div
              v-for="(seg, i) in segments"
              :key="i"
              class="segment-label"
              :style="`transform: rotate(${seg.mid}deg) translate(0, -${segmentOffset}px)`"
            >
              <img
                :src="seg.image"
                class="segment-image"
                :style="`transform: rotate(${-seg.mid}deg)`"
              />
              <span
                class="quantity"
                :style="`transform: rotate(${-seg.mid}deg)`"
              >
                x{{ seg.quantity }}
              </span>
            </div>
          </div>

          <img :src="roletaBatImg" class="roleta-bat" alt="Roleta Bat" />

          <div class="pointer-container">
            <img :src="pointerImg" class="pointer" alt="Pointer" />
            <div class="pointer-glow" :class="{ pulsing: isSpinning }"></div>
          </div>
        </div>

        <button
          class="spin-button"
          @click="spin"
          :disabled="
            isSpinning ||
            !user ||
            !selectedCurrency ||
            user[selectedCurrency] < spinCost
          "
        >
          <img :src="spinButtonImg" :alt="t('roulette.spinButtonAlt')" />
        </button>
      </div>
    </div>

    <!-- Diálogo de vitória -->
    <v-dialog v-model="showModal" persistent max-width="400">
      <v-card>
        <v-card-title class="headline">
          {{ t("roulette.congratsTitle") }}
        </v-card-title>
        <v-card-text>
          {{ t("roulette.congratsMessage", { quantity: lastWon.quantity, name: lastWon.name }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="closeModal">
            {{ t("roulette.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo de saldo insuficiente -->
    <v-dialog v-model="showInsufficientModal" persistent max-width="400">
      <v-card>
        <v-card-title class="headline">
          {{ t("roulette.insufficientTitle") }}
        </v-card-title>
        <v-card-text>
          {{ t("roulette.insufficientMessage", { cost: spinCost }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="closeInsufficientModal">
            {{ t("roulette.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { fetchPlayableCharacters, playRoleta } from "@/services/api";

import backgroundImg from "@/assets/background/cassino/cassinoz.png";
import wheelImg from "@/assets/roleta/roleta2.png";
import zephyrCoinsImg from "@/assets/roleta/roleta4.png";
import bonusCoinsImg from "@/assets/roleta/roleta3.png";
import spinButtonImg from "@/assets/roleta/roleta5.png";
import pointerImg from "@/assets/roleta/roletaponteiro.png";
import roletaBatImg from "@/assets/roleta/roletamorcego.png";

import barroImg from "@/assets/barro.png";
import bencaoImg from "@/assets/bencao.png";
import gaiaImg from "@/assets/gaia.png";

const { t } = useI18n();

const fullSpins = 6;
const spinDuration = 5;

const wheelContainer = ref(null);
const user = ref(null);
const selectedCurrency = ref(null);
const isSpinning = ref(false);
const currentRotation = ref(0);
const lastWon = ref({ quantity: 0, name: "" });
const showModal = ref(false);
const showInsufficientModal = ref(false);

const COSTS = { pvalues: 500, bonus: 1000 };
const spinCost = computed(() =>
  selectedCurrency.value ? COSTS[selectedCurrency.value] : 0
);

const rawItems = [
  {
    id: 48906,
    name: "Barro",
    image: barroImg,
    options: [
      { quantity: 5, chance: 90 },
      { quantity: 10, chance: 20 },
      { quantity: 25, chance: 50 },
      { quantity: 100, chance: 2 }
    ]
  },
  {
    id: 48700,
    name: "Bênção",
    image: bencaoImg,
    options: [
      { quantity: 5, chance: 90 },
      { quantity: 10, chance: 40 },
      { quantity: 25, chance: 70 }
    ]
  },
  {
    id: 40458,
    name: "Gaia",
    image: gaiaImg,
    options: [
      { quantity: 10, chance: 80 },
      { quantity: 25, chance: 60 }
    ]
  }
];

const items = computed(() =>
  rawItems.flatMap(item =>
    item.options.map(opt => ({
      id: item.id,
      name: item.name,
      image: item.image,
      quantity: opt.quantity,
      chance: opt.chance
    }))
  )
);

const segments = computed(() => {
  const n = items.value.length;
  const slice = 360 / n;
  return items.value.map((seg, i) => ({
    ...seg,
    mid: i * slice + slice / 2
  }));
});

const radius = ref(0);
const segmentOffset = ref(0);

function updateDimensions() {
  if (!wheelContainer.value) return;
  const rect = wheelContainer.value.getBoundingClientRect();
  radius.value = (rect.width / 2) * 0.6;
  segmentOffset.value = radius.value * 0.9;
}

function onResize() {
  updateDimensions();
}

function pickWeightedIndex() {
  const total = items.value.reduce((sum, it) => sum + it.chance, 0);
  let rand = Math.random() * total;
  for (let i = 0; i < items.value.length; i++) {
    rand -= items.value[i].chance;
    if (rand < 0) return i;
  }
  return 0;
}

async function spin() {
  if (!user.value || !selectedCurrency.value || isSpinning.value) return;
  if (user.value[selectedCurrency.value] < spinCost.value) {
    showInsufficientModal.value = true;
    return;
  }

  isSpinning.value = true;
  const idx = pickWeightedIndex();
  const seg = segments.value[idx];

  try {
    const token = localStorage.getItem("jwt_token");
    const funds = await playRoleta(
      token,
      seg.id,
      seg.quantity,
      selectedCurrency.value
    );
    user.value.pvalues = funds.pvalues;
    user.value.bonus = funds.bonus;
  } catch (err) {
    console.error("Erro ao jogar roleta:", err);
    isSpinning.value = false;
    return;
  }

  currentRotation.value = fullSpins * 360 + (360 - seg.mid);
  await nextTick();
  const rotatorEl = wheelContainer.value.querySelector(".rotating-circle");
  rotatorEl.addEventListener(
    "transitionend",
    () => {
      rotatorEl.style.transition = "none";
      currentRotation.value = 360 - seg.mid;
      void rotatorEl.offsetWidth;
      rotatorEl.style.transition = `transform ${spinDuration}s cubic-bezier(0.33,1,0.68,1)`;
      isSpinning.value = false;
      lastWon.value = { quantity: seg.quantity, name: seg.name };
      showModal.value = true;
    },
    { once: true }
  );
}

function closeModal() {
  showModal.value = false;
}
function closeInsufficientModal() {
  showInsufficientModal.value = false;
}

onMounted(async () => {
  updateDimensions();
  window.addEventListener("resize", onResize);
  try {
    const token = localStorage.getItem("jwt_token");
    const { user: u } = await fetchPlayableCharacters(token);
    user.value = u;
  } catch (err) {
    console.error("Erro ao buscar saldo:", err);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
});
</script>

<style scoped>
.main-container {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}
.background-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}
.main-title {
  position: relative;
  z-index: 2;
  text-align: center;
  font-size: 48px;
  color: #fff;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.8);
  margin-top: 20px;
}
.main-title-sub {
  position: absolute;
  z-index: 2;
  text-align: center;
  font-size: 20px;
  color: #fff;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.8);
  margin-top: 20px;
}
.main-title-sub1 {
  padding-top: 40px;
  position: absolute;
  z-index: 2;
  text-align: center;
  font-size: 20px;
  color: #fff;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.8);
  margin-top: 20px;
}
.content-wrapper {
  position: relative;
  z-index: 1;
  display: flex;
  width: 100%;
  height: calc(100% - 100px);
  padding: 20px;
  box-sizing: border-box;
}
.left-section {
  width: 30%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-right: 20px;
  gap: 30px;
}
.right-section {
  width: 70%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
}

/* ROLETE */
.wheel-container {
  position: relative;
  width: 575px;
  height: 575px;
  margin: 20px 0 20px -50px;
}
.wheel {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

/* círculo transparente com pivô exato */
.rotating-circle {
  position: absolute;
  top: 100px;
  left: 87px;
  width: 70%;
  height: 70%;
  border-radius: 50%;
  pointer-events: none;
  transform-origin: center center;
  transform-box: fill-box;
  z-index: 2;
}

/* divisores */
.segment-lines {
  position: absolute;
  top: 0%;
  left: 0%;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: 0.5;
  background: linear-gradient(to-bottom, rgba(127, 0, 128, 1), rgba(8, 0, 58, 1));
}

/* labels */
.segment-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform-origin: 10% 2%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 3;
}
.segment-image {
  width: 30px;
  margin-bottom: 4px;
}
.quantity {
  font-size: 1rem;
  font-weight: bold;
  color: #fff;
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.7);
}

/* morcego fixo */
.roleta-bat {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 150px;
  z-index: 4;
  pointer-events: none;
}

/* ponteiro fixo */
.pointer-container {
  position: absolute;
  top: -0;
  left: 49%;
  transform: translateX(-50%);
  width: 140px;
  z-index: 5;
}
.pointer {
  width: 100%;
  height: auto;
  position: relative;
  z-index: 6;
}
.pointer-glow {
  position: absolute;
  top: -10%;
  left: 53%;
  transform: translateX(-50%);
  width: 100%;
  height: 20ch;
}
@keyframes pulse-rise {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(0) scale(1);
  }
  100% {
    opacity: 0.8;
    transform: translateX(-50%) translateY(-20px) scale(1.2);
  }
}

/* botões */
.spin-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-top: 20px;
  margin-left: 160px;
  transition: transform 0.3s;
}
.spin-button img {
  width: 150px;
}
.spin-button:hover:not(:disabled) {
  transform: scale(1.05);
}
.spin-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* currency */
.currency-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: transform 0.3s;
  width: 80%;
  max-width: 250px;
}
.currency-button img {
  width: 100%;
}
.currency-button.active {
  transform: scale(1.1);
  filter: drop-shadow(0 0 10px gold);
}
.currency-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* info usuário */
.user-info {
  background: rgba(0, 0, 0, 0.7);
  padding: 15px;
  border-radius: 10px;
  color: #fff;
  width: 100%;
  max-width: 250px;
  text-align: center;
}
.user-info p,
.user-info li {
  margin: 5px 0;
}
.user-info ul {
  padding-left: 20px;
  list-style: none;
}

/* título secundário */
.text-h4 {
  color: #fff;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
  text-align: center;
  width: 100%;
}

/* === RESPONSIVIDADE MOBILE === */
@media (max-width: 768px) {
  .content-wrapper {
    flex-direction: column-reverse;
  }
  .left-section,
  .right-section {
    width: 100%;
    padding: 0;
  }
  .left-section {
    flex-direction: row;
    justify-content: space-around;
    margin-top: 20px;
    gap: 10px;
  }
  .right-section {
    align-items: center;
  }

  /* Ajuste do container da roda para mobile (tudo relativo à largura) */
  .wheel-container {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Reposiciona e redimensiona o círculo rotativo para sempre centralizar */
  .rotating-circle {
    position: relative; /* não mais absolute */
    top: auto;
    left: auto;
    width: 30%;
    height: 300%;
    transform-origin: center center;
    /* não declare transform aqui */
  }

  /* Segment lines e labels centralizados */
  .segment-lines {
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  .segment-label {
    top: 50%;
    left: 50%;
    transform-origin: center center !important;
  }
  .segment-image {
    width: 20px !important;
  }
  .quantity {
    font-size: 0.8rem !important;
  }

  /* Bat e ponteiro menores */
  .roleta-bat {
    width: 60px;
  }
  .pointer-container {
    width: 80px;
  }
  .pointer-glow {
    width: 80px;
    height: 80px;
  }

  .spin-button {
    margin-left: 0;
  }
  .currency-button {
    width: 45%;
    max-width: 150px;
  }
  .spin-button img {
    width: 120px;
  }
  .user-info {
    display: none;
  }
  .main-title {
    font-size: 32px;
    margin-top: 10px;
  }
}
</style>
